/**
 * ============================================================================
 * useCacheInvalidation - Centralized cache invalidation for cross-tab updates
 * ============================================================================
 *
 * When a booking is created/cancelled/modified, multiple tabs need to update:
 * - Bookings tab: The booking list
 * - Revenue tab: Earnings calculations
 * - Customer Insights: Customer statistics
 * - Transactions: Ledger entries
 * - Court Schedules: Today's availability
 *
 * This hook provides a centralized way to invalidate all related caches.
 * ============================================================================
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

type InvalidationType =
  | 'booking'           // Booking created, cancelled, rescheduled
  | 'court'             // Court added, updated, deleted
  | 'venue'             // Venue info updated
  | 'promotion'         // Promotion created/updated
  | 'membership'        // Membership created/updated
  | 'payout'            // Payout requested
  | 'all';              // Full refresh

/**
 * Maps invalidation types to the query keys that need to be refreshed
 */
const INVALIDATION_MAP: Record<InvalidationType, string[]> = {
  booking: [
    'bookings',           // Booking list
    'schedule',           // Court schedule/availability
    'court-schedules',    // Today's field/court schedule
    'earnings',           // Revenue calculations
    'peakHours',          // Peak hours in revenue tab
    'manual-booking-customers', // Customer autocomplete dropdown
    'customer-insights',  // Customer statistics
    'ledger-transactions',// Transaction ledger
    'recent-transactions',// Dashboard recent transactions
    'dashboard',          // Dashboard stats
  ],
  court: [
    'courts',             // Courts list
    'court-schedules',    // Court schedules
    'schedule',           // Availability schedule
    'venue-info',         // Venue info includes courts
  ],
  venue: [
    'venue-info',         // Venue details
    'dashboard',          // Dashboard data
    'operating-hours',    // Operating hours
  ],
  promotion: [
    'promotions',         // Promotions list
  ],
  membership: [
    'memberships',        // Memberships list
    'earnings',           // Revenue includes memberships
    'ledger-transactions',// Transaction ledger
  ],
  payout: [
    'earnings',           // Earnings summary
    'ledger-transactions',// Transaction ledger
    'payout-history',     // Payout history
  ],
  all: [
    'bookings',
    'schedule',
    'court-schedules',
    'courts',
    'earnings',
    'peakHours',
    'manual-booking-customers',
    'customer-insights',
    'ledger-transactions',
    'recent-transactions',
    'dashboard',
    'venue-info',
    'promotions',
    'memberships',
    'payout-history',
  ],
};

/**
 * Hook for centralized cache invalidation
 */
export function useCacheInvalidation() {
  const queryClient = useQueryClient();

  /**
   * Invalidate all caches related to a specific action type
   */
  const invalidateRelated = useCallback(
    async (type: InvalidationType, venueId?: number) => {
      const queryKeys = INVALIDATION_MAP[type] || [];

      const invalidationPromises = queryKeys.map((key) => {
        // If venueId provided, invalidate specific venue queries
        // Otherwise invalidate all queries with that key prefix
        if (venueId) {
          return queryClient.invalidateQueries({
            queryKey: [key, venueId],
            exact: false, // Match any query starting with [key, venueId, ...]
          });
        }
        return queryClient.invalidateQueries({
          queryKey: [key],
          exact: false,
        });
      });

      await Promise.all(invalidationPromises);
    },
    [queryClient]
  );

  /**
   * Invalidate booking-related caches (most common operation)
   */
  const invalidateBookingRelated = useCallback(
    async (venueId: number) => {
      await invalidateRelated('booking', venueId);
    },
    [invalidateRelated]
  );

  /**
   * Invalidate court-related caches
   */
  const invalidateCourtRelated = useCallback(
    async (venueId: number) => {
      await invalidateRelated('court', venueId);
    },
    [invalidateRelated]
  );

  /**
   * Force refetch of specific queries (use after invalidation for immediate update)
   */
  const refetchQueries = useCallback(
    async (queryKeys: string[], venueId?: number) => {
      const refetchPromises = queryKeys.map((key) => {
        if (venueId) {
          return queryClient.refetchQueries({
            queryKey: [key, venueId],
            exact: false,
          });
        }
        return queryClient.refetchQueries({
          queryKey: [key],
          exact: false,
        });
      });

      await Promise.all(refetchPromises);
    },
    [queryClient]
  );

  return {
    invalidateRelated,
    invalidateBookingRelated,
    invalidateCourtRelated,
    refetchQueries,
    queryClient,
  };
}

export type { InvalidationType };
